import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import i18next from "i18next";
import moment from "moment";
import { z } from "zod";

import { UploadCertSchema } from "src/shared/api-types/certificate";
import type { UserCoursesUploadCert } from "src/shared/api-types/certificate";
import type { UserCrew } from "src/shared/api-types/user";

import type { UploadableCourse } from "src/features/bulk-certificate-upload";

import { NEVER_EXPIRES_SENTINEL, usePositionValidPeriods } from "./usePositionValidPeriods";
import type { PeriodsResult } from "./usePositionValidPeriods";

export type UserCoursesSubmitPayload = UserCoursesUploadCert & { isCustom: boolean; files: File[] };

export interface UserCoursesFormInput {
  selectedUser: UserCrew | null;
  courses: UploadableCourse[];
  files: File[];
  completed: string;
  validUntil: string | null;
  comment: string;
  neverExpires: boolean;
}

const createFormSchema = (crewId: number) =>
  UploadCertSchema.pick({ completed: true, validUntil: true, comment: true })
    .extend({
      selectedUser: z.custom<UserCrew | null>((v) => v !== null && v !== undefined, {
        message: i18next.t("zod_errors.required"),
      }),
      courses: z.array(z.custom<UploadableCourse>()).min(1),
      files: z.array(z.instanceof(File)).min(1),
      neverExpires: z.boolean(),
    })
    .refine((v) => v.courses.every((course) => course.isCustom === v.courses[0]?.isCustom), {
      message: i18next.t("certificate_upload.bulk.single_user.mixed_certificate_types"),
      path: ["courses"],
    })
    .refine((v) => v.neverExpires || v.validUntil != null, {
      message: i18next.t("zod_errors.required"),
      path: ["validUntil"],
    })
    .transform(
      (v): UserCoursesSubmitPayload => ({
        crewId,
        userId: v.selectedUser!.id,
        courseIds: v.courses.map((course) => course.id),
        isCustom: v.courses[0].isCustom,
        files: v.files,
        completed: v.completed,
        validUntil: v.neverExpires ? null : v.validUntil,
        comment: v.comment,
      }),
    );

export function useUserCoursesCertificateForm(crewId: number) {
  const form = useForm<UserCoursesFormInput, unknown, UserCoursesSubmitPayload>({
    resolver: zodResolver(createFormSchema(crewId)) as any,
    mode: "onChange",
    defaultValues: {
      selectedUser: null,
      courses: [],
      files: [],
      completed: "",
      validUntil: null,
      comment: "",
      neverExpires: false,
    },
  });

  const { watch, setValue, getValues } = form;
  const completedDate = watch("completed") ?? "";
  const neverExpires = watch("neverExpires") ?? false;
  const selectedUser = watch("selectedUser");
  const selectedCourses = watch("courses") ?? [];

  const courseTargets = useMemo(
    () =>
      selectedUser
        ? selectedCourses.map((course) => ({
            courseId: course.id,
            isCustom: course.isCustom,
            userId: selectedUser.id,
            courseDefaultMonths: course.validityPeriodMonths,
          }))
        : [],
    [selectedCourses, selectedUser],
  );

  const validityHook = usePositionValidPeriods(crewId, 0, false, [], null, courseTargets);
  const { resolvedMonths, divergentPeriods, allFromPositionOverride, allNeverExpires } = validityHook.getResult();
  const courseDefaultMonths = selectedCourses[0]?.validityPeriodMonths ?? null;
  const courseIsNeverExpires =
    selectedCourses.length > 0 && selectedCourses.every((course) => course.validityPeriodMonths === 0);
  const isDivergent = divergentPeriods !== null;
  const effectiveNeverExpires = allNeverExpires || (courseIsNeverExpires && !allFromPositionOverride && !isDivergent);

  // The last validUntil derived from completedDate, so it can be withdrawn without touching a date the
  // user typed into Valid Until themselves.
  const autoValidUntil = useRef<string | null>(null);

  useEffect(() => {
    if (neverExpires) return;
    if (!completedDate) {
      // The completion date was cleared or rejected: a Valid Until derived from it is stale.
      if (autoValidUntil.current !== null && getValues("validUntil") === autoValidUntil.current) {
        setValue("validUntil", null, { shouldValidate: true });
      }
      autoValidUntil.current = null;
      return;
    }
    const { resolvedMonths } = validityHook.getResult();
    if (!resolvedMonths) return;
    const m = moment(completedDate, "YYYY-MM-DD");
    if (m.isValid()) {
      const validUntil = m.clone().add(resolvedMonths, "months").endOf("month").format("YYYY-MM-DD");
      autoValidUntil.current = validUntil;
      setValue("validUntil", validUntil, { shouldValidate: true });
    }
  }, [completedDate]);

  useEffect(() => {
    if (neverExpires) {
      setValue("validUntil", null);
    }
  }, [neverExpires]);

  useEffect(() => {
    if (effectiveNeverExpires) {
      setValue("neverExpires", true, { shouldValidate: true });
    } else if (resolvedMonths !== null || isDivergent) {
      setValue("neverExpires", false, { shouldValidate: true });
    }
  }, [effectiveNeverExpires, resolvedMonths, isDivergent]);

  const getTooltipContent = (t: (key: string, opts?: object) => string): string => {
    if (divergentPeriods) {
      const parts = Array.from(divergentPeriods.entries())
        .sort(([a], [b]) => a - b)
        .map(([months, count]) =>
          months === NEVER_EXPIRES_SENTINEL
            ? t("certificate_upload.validity_period_certificate_never_expires_count", { count })
            : t("certificate_upload.validity_period_certificate_count", { months, count }),
        );
      return t("certificate_upload.validity_period_certificates_divergent", { breakdown: parts.join(", ") });
    }
    if (effectiveNeverExpires) return t("validity_period_never_expires");
    if (!resolvedMonths) return t("validity_period");
    if (allFromPositionOverride && resolvedMonths !== courseDefaultMonths) {
      return t("validity_period_position_override", {
        positionName: t("certificate_upload.bulk.validity_period_position_override"),
        positionMonths: resolvedMonths,
        courseMonths: courseDefaultMonths ?? 0,
      });
    }
    return t("validity_period_course_default", { months: resolvedMonths });
  };

  return {
    form,
    validityHook,
    neverExpires,
    periodsResult: { resolvedMonths, divergentPeriods, allFromPositionOverride, allNeverExpires } as PeriodsResult,
    effectiveNeverExpires,
    getTooltipContent,
  };
}
